import React, { useRef, useState, Children } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useClipboard } from "../../hooks/useClipboard";

function MdParagraph({ children, ...props }: React.HTMLAttributes<HTMLElement>) {
  const kids = Children.toArray(children);
  const allImages = kids.every(
    (child) => React.isValidElement(child) && child.type === "img"
  );
  if (allImages && kids.length > 0) {
    return <div className="md-image-group">{children}</div>;
  }
  return <p {...props}>{children}</p>;
}

export const MarkdownPreviewTool: React.FC = () => {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [fullscreen, setFullscreen] = useState(false);
  const { copy, copied } = useClipboard();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileLoad = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setText((ev.target?.result as string) ?? "");
    reader.readAsText(file, "utf-8");
    // Reset input so the same file can be re-loaded
    e.target.value = "";
  };

  // Close fullscreen on Escape
  React.useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  return (
    <div className={`preview-tool${fullscreen ? " preview-tool--fullscreen" : ""}`}>
      {/* Fullscreen toolbar */}
      {fullscreen && (
        <div className="preview-tool__fullscreen-bar">
          <span className="preview-tool__fullscreen-title">{t("mode.preview")}</span>
          <button
            className="btn btn--ghost btn--sm"
            onClick={() => setFullscreen(false)}
            title="Exit fullscreen (Esc)"
          >
            ✕ Exit
          </button>
        </div>
      )}

      <div className="preview-tool__panes">
        <div className="preview-tool__pane">
          <div className="preview-tool__pane-header">
            <span>{t("preview.inputLabel")}</span>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              {text && <span className="preview-tool__count">{text.length} chars</span>}
              <button
                className="btn btn--ghost btn--sm"
                onClick={() => fileInputRef.current?.click()}
                title={t("preview.loadFile")}
              >
                ↑ {t("preview.loadFile")}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".md,.markdown,.txt"
                style={{ display: "none" }}
                onChange={handleFileLoad}
              />
              {!fullscreen && (
                <button
                  className="btn btn--ghost btn--sm"
                  onClick={() => setFullscreen(true)}
                  title="Expand fullscreen"
                >
                  ⤢
                </button>
              )}
            </div>
          </div>
          <textarea
            className="preview-tool__textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("preview.placeholder")}
            spellCheck={false}
          />
        </div>

        <div className="preview-tool__pane">
          <div className="preview-tool__pane-header">
            <span>{t("preview.outputLabel")}</span>
            {text && (
              <button className="btn btn--ghost btn--sm" onClick={() => copy(text)}>
                {copied ? t("actions.copied") : t("actions.copy")}
              </button>
            )}
          </div>
          <div className="preview-tool__rendered markdown-body">
            {text ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                urlTransform={(url) => url}
                components={{ p: MdParagraph as any }}
              >
                {text}
              </ReactMarkdown>
            ) : (
              <p className="preview-tool__empty">{t("preview.empty")}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
